import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../context/AuthContext';
import { configService } from '../services/configService';
import { toast } from '../utils/toast';

export const LAIKA_CONSENT_VERSION = 'laika-data-access-v1';

export interface UseLaikaConsentResult {
  isEnabled: boolean;
  acceptedAt: string | null;
  consentVersion: string | null;
  isSubmitting: boolean;
  updateConsent: (enabled: boolean) => Promise<boolean>;
}

export function useLaikaConsent(): UseLaikaConsentResult {
  const { t } = useTranslation('messaging');
  const { user, refreshUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnabled = !!user?.laika_data_access_enabled;
  const acceptedAt = user?.laika_data_access_accepted_at ?? null;
  const consentVersion = user?.laika_data_access_consent_version ?? null;

  const updateConsent = useCallback(async (enabled: boolean): Promise<boolean> => {
    if (!user?.pro_id) {
      toast.error(t('laikaConsentToasts.proNotFound'));
      return false;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('laikaConsentToasts.sessionExpired'));
      return false;
    }

    setIsSubmitting(true);
    try {
      const endpoint = configService.getFullApiUrl(`/pros/${user.pro_id}/laika-data-access`);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled,
          consent_version: LAIKA_CONSENT_VERSION,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || t('laikaConsentToasts.updateImpossible'));
      }

      await refreshUser();
      toast.success(
        enabled
          ? t('laikaConsentToasts.enabled')
          : t('laikaConsentToasts.disabled')
      );
      return true;
    } catch {
      toast.error(t('laikaConsentToasts.updateError'));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [user?.pro_id, refreshUser, t]);

  return { isEnabled, acceptedAt, consentVersion, isSubmitting, updateConsent };
}
